/**
 * Celeste Base Component
 *
 * SVG component for player home base area using the celeste skin.
 * Features a sky-blue gradient frame with gold decorative elements,
 * cloud shapes, and player-specific gradient quadrants.
 *
 * This component is color-agnostic and accepts a colorScheme prop to render
 * bases for different players (Red, Blue, Yellow, Green).
 */

import React, { type ReactElement } from 'react';
import type { BaseProps } from '../../constants/types';
import type { PawnProps } from '../../constants/types';
import { renderPawnAtPosition } from '../basics/utils/utils';
import type { CelesteColorScheme } from './constants/baseColorSchemes';

// Helper constants for rank display
const RANK_ICONS = ['🏆', '🥈', '🥉', '🏅'] as const;
const RANK_ORDINALS = ['1st', '2nd', '3rd', '4th'] as const;

function getRankIcon(rank: number): string {
  return RANK_ICONS[Math.min(rank - 1, 3)];
}

function getRankOrdinal(rank: number): string {
  if (rank >= 1 && rank <= 4) {
    return RANK_ORDINALS[rank - 1];
  }
  return `${rank.toString()}th`;
}

export interface CelesteBaseComponentProps extends BaseProps {
  colorScheme: CelesteColorScheme;
  PawnComponent: React.ComponentType<PawnProps>;
  onPawn1Click?: () => void;
  onPawn2Click?: () => void;
  onPawn3Click?: () => void;
  onPawn4Click?: () => void;
  shouldAnimatePawns?: boolean;
  /** Player's finishing rank (1-4) - when set, shows rank badge instead of pawns */
  playerRank?: number;
  /** Whether the player has voluntarily left the game */
  hasLeft?: boolean;
}

const CelesteBaseComponent = React.memo(function CelesteBaseComponent({
  viewBox = '0 0 486 523',
  className = '',
  isActive = false,
  isHovered = false,
  isSelected = false,
  showEntrance = false,
  pawn1 = false,
  pawn2 = false,
  pawn3 = false,
  pawn4 = false,
  pawn1Active = false,
  pawn1Hovered = false,
  pawn1Selected = false,
  pawn1Moveable = false,
  pawn2Active = false,
  pawn2Hovered = false,
  pawn2Selected = false,
  pawn2Moveable = false,
  pawn3Active = false,
  pawn3Hovered = false,
  pawn3Selected = false,
  pawn3Moveable = false,
  pawn4Active = false,
  pawn4Hovered = false,
  pawn4Selected = false,
  pawn4Moveable = false,
  colorScheme,
  PawnComponent,
  onPawn1Click,
  onPawn2Click,
  onPawn3Click,
  onPawn4Click,
  shouldAnimatePawns = false,
  playerRank,
  hasLeft = false
}: CelesteBaseComponentProps): ReactElement {
  const { name, ariaLabel, gradientStart, gradientMid, gradientEnd } = colorScheme;

  const svgClassName = [
    className,
    showEntrance ? 'base-entrance' : '',
    isActive ? 'base-active' : '',
    isHovered ? 'base-hovered' : '',
    isSelected ? 'base-selected' : ''
  ]
    .filter(Boolean)
    .join(' ');

  // Prefix all gradient/clip IDs to avoid conflicts when 4 bases render simultaneously
  const prefix = `${name}Celeste`;

  return (
    <svg
      viewBox={viewBox}
      className={svgClassName}
      xmlns="http://www.w3.org/2000/svg"
      aria-label={ariaLabel}
    >
      <defs>
        {/* paint0 — Shadow dark: #333333 → #1A1A1A */}
        <linearGradient
          id={`${prefix}P0`}
          x1="245.02"
          y1="443.37"
          x2="245.02"
          y2="475.24"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#333333" />
          <stop offset="1" stopColor="#1A1A1A" />
        </linearGradient>

        {/* paint1 — Shadow light: #F2F2F2 → #808080 */}
        <linearGradient
          id={`${prefix}P1`}
          x1="245.04"
          y1="431.71"
          x2="245.04"
          y2="462.78"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#F2F2F2" />
          <stop offset="1" stopColor="#808080" />
        </linearGradient>

        {/* paint2 — Frame gradient: #72BBFC → #A7DFFC → #E6FBFF */}
        <linearGradient
          id={`${prefix}P2`}
          x1="440.59"
          y1="246.46"
          x2="48.53"
          y2="246.46"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#72BBFC" />
          <stop offset="0.48" stopColor="#A7DFFC" />
          <stop offset="1" stopColor="#E6FBFF" />
        </linearGradient>

        {/* paint3 — Gold star decoration: #FECB70 → #FDE58F → #FEF5B2 */}
        <linearGradient
          id={`${prefix}P3`}
          x1="65.29"
          y1="60.2"
          x2="31.03"
          y2="60.2"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FECB70" />
          <stop offset="0.48" stopColor="#FDE58F" />
          <stop offset="1" stopColor="#FEF5B2" />
        </linearGradient>

        {/* paint4 — Gold quadrant border bottom-left: #FECB70 → #FDE58F → #FEF5B2 */}
        <linearGradient
          id={`${prefix}P4`}
          x1="105.49"
          y1="313.16"
          x2="245.02"
          y2="313.16"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FECB70" />
          <stop offset="0.48" stopColor="#FDE58F" />
          <stop offset="1" stopColor="#FEF5B2" />
        </linearGradient>

        {/* paint5 — Gold quadrant border top-right: #FECB70 → #FDE58F → #FEF5B2 */}
        <linearGradient
          id={`${prefix}P5`}
          x1="245.04"
          y1="179.75"
          x2="384.57"
          y2="179.75"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FECB70" />
          <stop offset="0.48" stopColor="#FDE58F" />
          <stop offset="1" stopColor="#FEF5B2" />
        </linearGradient>

        {/* paint6 — Player quadrant top-left (player-specific) */}
        <linearGradient
          id={`${prefix}P6`}
          x1="105.5"
          y1="179.75"
          x2="245.04"
          y2="179.75"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={gradientStart} />
          <stop offset="0.48" stopColor={gradientMid} />
          <stop offset="1" stopColor={gradientEnd} />
        </linearGradient>

        {/* paint7 — Player quadrant bottom-right (player-specific) */}
        <linearGradient
          id={`${prefix}P7`}
          x1="245.04"
          y1="313.16"
          x2="384.57"
          y2="313.16"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={gradientStart} />
          <stop offset="0.48" stopColor={gradientMid} />
          <stop offset="1" stopColor={gradientEnd} />
        </linearGradient>

        {/* paint8 — Cloud top-right: #A7DFFC → #E6FBFF */}
        <linearGradient
          id={`${prefix}P8`}
          x1="333.43"
          y1="94.8401"
          x2="441.83"
          y2="94.8401"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#A7DFFC" />
          <stop offset="1" stopColor="#E6FBFF" />
        </linearGradient>

        {/* paint9 — Cloud right-edge: #A7DFFC → #E6FBFF */}
        <linearGradient
          id={`${prefix}P9`}
          x1="441.85"
          y1="158.21"
          x2="393.89"
          y2="158.21"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#A7DFFC" />
          <stop offset="1" stopColor="#E6FBFF" />
        </linearGradient>

        {/* paint10 — Cloud small right: #A7DFFC → #E6FBFF */}
        <linearGradient
          id={`${prefix}P10`}
          x1="390.459"
          y1="222.87"
          x2="434.77"
          y2="204.289"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#A7DFFC" />
          <stop offset="1" stopColor="#E6FBFF" />
        </linearGradient>

        {/* paint11 — Cloud bottom-left: #A7DFFC → #E6FBFF */}
        <linearGradient
          id={`${prefix}P11`}
          x1="53.73"
          y1="402.99"
          x2="162.13"
          y2="402.99"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#A7DFFC" />
          <stop offset="1" stopColor="#E6FBFF" />
        </linearGradient>

        {/* paint12 — Cloud small top: #A7DFFC → #E6FBFF */}
        <linearGradient
          id={`${prefix}P12`}
          x1="290.088"
          y1="90.575"
          x2="325.777"
          y2="78.8343"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#A7DFFC" />
          <stop offset="1" stopColor="#E6FBFF" />
        </linearGradient>

        {/* paint13 — Cloud small bottom-center: #A7DFFC → #E6FBFF */}
        <linearGradient
          id={`${prefix}P13`}
          x1="170.378"
          y1="407.549"
          x2="206.066"
          y2="395.809"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#A7DFFC" />
          <stop offset="1" stopColor="#E6FBFF" />
        </linearGradient>

        {/* paint14 — Cloud tiny left: #A7DFFC → #E6FBFF */}
        <linearGradient
          id={`${prefix}P14`}
          x1="73.8556"
          y1="350.916"
          x2="100.605"
          y2="342.116"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#A7DFFC" />
          <stop offset="1" stopColor="#E6FBFF" />
        </linearGradient>

        {/* paint15 — Cloud left-edge: #A7DFFC → #E6FBFF */}
        <linearGradient
          id={`${prefix}P15`}
          x1="93.98"
          y1="314.71"
          x2="48.22"
          y2="314.71"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#A7DFFC" />
          <stop offset="1" stopColor="#E6FBFF" />
        </linearGradient>

        {/* paint16 — Gold decoration bottom-left diagonal 1: #FECB70 → #FDE58F → #FEF5B2 */}
        <linearGradient
          id={`${prefix}P16`}
          x1="41.1"
          y1="100.32"
          x2="102.88"
          y2="100.32"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FECB70" />
          <stop offset="0.48" stopColor="#FDE58F" />
          <stop offset="1" stopColor="#FEF5B2" />
        </linearGradient>

        {/* paint17 — Gold decoration bottom-left diagonal 2: #FECB70 → #FDE58F → #FEF5B2 */}
        <linearGradient
          id={`${prefix}P17`}
          x1="65.4"
          y1="84.65"
          x2="111.25"
          y2="84.65"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FECB70" />
          <stop offset="0.48" stopColor="#FDE58F" />
          <stop offset="1" stopColor="#FEF5B2" />
        </linearGradient>

        {/* paint18 — Gold decoration bottom-left vertical: #FECB70 → #FDE58F → #FEF5B2 */}
        <linearGradient
          id={`${prefix}P18`}
          x1="90.57"
          y1="441.65"
          x2="90.57"
          y2="379.87"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FECB70" />
          <stop offset="0.48" stopColor="#FDE58F" />
          <stop offset="1" stopColor="#FEF5B2" />
        </linearGradient>

        {/* paint19 — Gold decoration bottom-left corner: #FECB70 → #FDE58F → #FEF5B2 */}
        <linearGradient
          id={`${prefix}P19`}
          x1="74.9"
          y1="417.35"
          x2="74.9"
          y2="371.5"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FECB70" />
          <stop offset="0.48" stopColor="#FDE58F" />
          <stop offset="1" stopColor="#FEF5B2" />
        </linearGradient>

        {/* paint20 — Gold decoration bottom-right vertical: #FECB70 → #FDE58F → #FEF5B2 */}
        <linearGradient
          id={`${prefix}P20`}
          x1="400.62"
          y1="441.65"
          x2="400.62"
          y2="379.87"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FECB70" />
          <stop offset="0.48" stopColor="#FDE58F" />
          <stop offset="1" stopColor="#FEF5B2" />
        </linearGradient>

        {/* paint21 — Gold decoration bottom-right corner: #FECB70 → #FDE58F → #FEF5B2 */}
        <linearGradient
          id={`${prefix}P21`}
          x1="416.3"
          y1="417.35"
          x2="416.3"
          y2="371.5"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FECB70" />
          <stop offset="0.48" stopColor="#FDE58F" />
          <stop offset="1" stopColor="#FEF5B2" />
        </linearGradient>

        {/* paint22 — Gold decoration top-right diagonal 1: #FECB70 → #FDE58F → #FEF5B2 */}
        <linearGradient
          id={`${prefix}P22`}
          x1="446.02"
          y1="100.81"
          x2="384.24"
          y2="100.81"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FECB70" />
          <stop offset="0.48" stopColor="#FDE58F" />
          <stop offset="1" stopColor="#FEF5B2" />
        </linearGradient>

        {/* paint23 — Gold decoration top-right diagonal 2: #FECB70 → #FDE58F → #FEF5B2 */}
        <linearGradient
          id={`${prefix}P23`}
          x1="421.72"
          y1="85.13"
          x2="375.87"
          y2="85.13"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FECB70" />
          <stop offset="0.48" stopColor="#FDE58F" />
          <stop offset="1" stopColor="#FEF5B2" />
        </linearGradient>

        {/* paint24 — Gold star top-right: #FECB70 → #FDE58F → #FEF5B2 */}
        <linearGradient
          id={`${prefix}P24`}
          x1="420.46"
          y1="60.2"
          x2="454.72"
          y2="60.2"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FECB70" />
          <stop offset="0.48" stopColor="#FDE58F" />
          <stop offset="1" stopColor="#FEF5B2" />
        </linearGradient>

        {/* paint25 — Gold star bottom-left: #FECB70 → #FDE58F → #FEF5B2 */}
        <linearGradient
          id={`${prefix}P25`}
          x1="65.29"
          y1="441.96"
          x2="31.03"
          y2="441.96"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FECB70" />
          <stop offset="0.48" stopColor="#FDE58F" />
          <stop offset="1" stopColor="#FEF5B2" />
        </linearGradient>

        {/* paint26 — Gold star bottom-right: #FECB70 → #FDE58F → #FEF5B2 */}
        <linearGradient
          id={`${prefix}P26`}
          x1="420.46"
          y1="441.96"
          x2="454.72"
          y2="441.96"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FECB70" />
          <stop offset="0.48" stopColor="#FDE58F" />
          <stop offset="1" stopColor="#FEF5B2" />
        </linearGradient>

        <clipPath id={`${prefix}Clip0`}>
          <rect width="485.33" height="522.29" fill="white" />
        </clipPath>
      </defs>

      {/*
        Translate celeste content up by ~15.8px to align the frame with the basics skin.
        The celeste frame starts at y≈57.7 while basics starts at y≈41.89.
        This ensures both skins occupy the same visual bounds within the viewBox,
        so board positioning and pawn placement remain consistent.
      */}
      <g clipPath={`url(#${prefix}Clip0)`} transform="translate(0, -15.8)">
        {/* Shadow bottom extrusion dark */}
        <path
          d="M441.8 452.35C441.83 455.45 440.78 458.48 438.75 460.82C438.75 460.84 438.72 460.86 438.71 460.87C438.71 460.87 433.72 467.47 426.76 470.28C418.25 473.71 411.64 470.93 401.33 470.79C390.45 470.64 386.29 473.57 374.63 476.31C357.78 480.26 354 477.05 306.49 476.31C305.36 476.29 294.55 476.28 272.93 476.26C258.81 476.25 239.06 476.23 219.02 476.26C191.14 476.3 163.25 476.25 135.37 476.26C121.37 476.26 107.39 474.21 93.41 474.86C81.94 475.39 72.45 476.61 62.39 471.3C62.32 471.26 62.25 471.22 62.18 471.19C53.51 466.53 48.24 457.33 48.24 447.49V413.83C100.78 420.27 159.85 424.6 224.4 424.64C243.39 424.65 261.93 424.29 279.99 423.6C339.17 421.37 393.25 415.72 441.38 408.49C441.46 416.77 441.54 425.06 441.62 433.34C441.62 433.73 441.63 434.12 441.63 434.51C441.66 437.89 441.69 441.27 441.73 444.65C441.75 447.22 441.78 449.78 441.8 452.35Z"
          fill={`url(#${prefix}P0)`}
        />
        {/* Shadow bottom extrusion light */}
        <path
          d="M48.23 393.73V438.14C50.93 442.14 55.4 447.5 62.38 451.19C72.44 456.5 81.93 455.28 93.4 454.75C107.38 454.1 121.36 456.16 135.35 456.15C163.23 456.15 191.12 456.19 219 456.15C239.04 456.12 258.79 456.14 272.91 456.15C294.53 456.17 305.35 456.18 306.47 456.2C353.98 456.94 357.76 460.15 374.61 456.2C386.26 453.47 390.43 450.53 401.31 450.68C411.62 450.82 418.22 453.6 426.74 450.17C433.7 447.36 438.69 440.76 438.69 440.76C440.09 439.14 441.13 437.67 441.83 436.61C441.67 420.53 441.52 404.46 441.36 388.38C378.53 397.81 305.57 404.57 224.38 404.53C159.82 404.49 100.75 400.16 48.21 393.72L48.23 393.73Z"
          fill={`url(#${prefix}P1)`}
        />
        {/* Main frame (sky-blue gradient) */}
        <path
          d="M441.85 90.75V402.17C441.85 420.42 427.05 435.22 408.8 435.22H81.27C63.01 435.22 48.21 420.42 48.21 402.17V90.75C48.21 72.49 63.01 57.7 81.27 57.7H408.8C427.05 57.7 441.85 72.49 441.85 90.75Z"
          fill={`url(#${prefix}P2)`}
        />
        {/* Gold star top-left — flat layer */}
        <path
          d="M48.2 54.64L65.4 46.73L57.49 63.93L65.4 81.13L48.2 73.22L31 81.13L38.91 63.93L31 46.73L48.2 54.64Z"
          fill="#D3A04F"
        />
        {/* Gold star top-left — cross layer */}
        <path
          d="M31 81.13V77.4L41.43 60.2L31 46.73V43L51.58 55.3L65.4 43V46.73L53.65 66.12L65.4 77.4V81.13L44.24 72.78L31 81.13Z"
          fill="#D3A04F"
        />
        {/* Gold star top-left — gradient layer */}
        <path
          d="M48.2 50.91L65.4 43L57.49 60.2L65.4 77.4L48.2 69.49L31 77.4L38.91 60.2L31 43L48.2 50.91Z"
          fill={`url(#${prefix}P3)`}
        />
        {/* Gold quadrant border bottom-left — flat */}
        <path
          d="M384.54 251.46V347.98C384.54 368.35 368.02 384.87 347.65 384.87H245V251.46H384.54Z"
          fill="#D3A04F"
        />
        {/* Gold quadrant border top-left — flat */}
        <path
          d="M105.46 251.46H244.99V384.86H142.35C121.98 384.86 105.46 368.35 105.46 347.98V251.46Z"
          fill="#D3A04F"
        />
        {/* Gold quadrant border bottom-left — gradient */}
        <path
          d="M105.49 246.46H245.02V379.86H142.37C122.01 379.86 105.48 363.33 105.48 342.97V246.46H105.49Z"
          fill={`url(#${prefix}P4)`}
        />
        {/* Gold quadrant border top-right — gradient */}
        <path
          d="M384.56 246.46L245.03 246.46L245.03 113.06L347.68 113.06C368.04 113.06 384.57 129.59 384.57 149.95V246.47L384.56 246.46Z"
          fill={`url(#${prefix}P5)`}
        />
        {/* Player quadrant top-left */}
        <path
          d="M245.03 113.06V246.46H105.5V149.94C105.5 129.58 122.03 113.05 142.39 113.05H245.03V113.06Z"
          fill={`url(#${prefix}P6)`}
        />
        {/* Player quadrant bottom-right */}
        <path
          d="M245.03 379.86V246.46H384.56V342.98C384.56 363.34 368.03 379.87 347.67 379.87H245.03V379.86Z"
          fill={`url(#${prefix}P7)`}
        />

        {/* Pawn spots — only visible when game is active (no rank, not left) */}
        {playerRank === undefined && !hasLeft && (
          <>
            {/* Pawn spot 1 — top-left shadow */}
            <path
              d="M175.28 213.9C195.72 213.9 212.29 198.449 212.29 179.39C212.29 160.331 195.72 144.88 175.28 144.88C154.84 144.88 138.27 160.331 138.27 179.39C138.27 198.449 154.84 213.9 175.28 213.9Z"
              fill="#44411F"
            />
            {/* Pawn spot 1 — top-left fill */}
            <path
              d="M175.28 216.59C195.72 216.59 212.29 201.139 212.29 182.08C212.29 163.021 195.72 147.57 175.28 147.57C154.84 147.57 138.27 163.021 138.27 182.08C138.27 201.139 154.84 216.59 175.28 216.59Z"
              fill="#FFFECE"
            />
            {/* Pawn spot 2 — top-right shadow */}
            <path
              d="M314.81 212.55C335.25 212.55 351.82 197.099 351.82 178.04C351.82 158.981 335.25 143.53 314.81 143.53C294.37 143.53 277.8 158.981 277.8 178.04C277.8 197.099 294.37 212.55 314.81 212.55Z"
              fill="#44411F"
            />
            {/* Pawn spot 2 — top-right fill */}
            <path
              d="M314.81 215.25C335.25 215.25 351.82 199.799 351.82 180.74C351.82 161.681 335.25 146.23 314.81 146.23C294.37 146.23 277.8 161.681 277.8 180.74C277.8 199.799 294.37 215.25 314.81 215.25Z"
              fill="#FFFECE"
            />
            {/* Pawn spot 3 — bottom-left shadow */}
            <path
              d="M175.26 345.85C195.7 345.85 212.27 330.399 212.27 311.34C212.27 292.281 195.7 276.83 175.26 276.83C154.82 276.83 138.25 292.281 138.25 311.34C138.25 330.399 154.82 345.85 175.26 345.85Z"
              fill="#44411F"
            />
            {/* Pawn spot 3 — bottom-left fill */}
            <path
              d="M175.26 348.54C195.7 348.54 212.27 333.089 212.27 314.03C212.27 294.971 195.7 279.52 175.26 279.52C154.82 279.52 138.25 294.971 138.25 314.03C138.25 333.089 154.82 348.54 175.26 348.54Z"
              fill="#FFFECE"
            />
            {/* Pawn spot 4 — bottom-right shadow */}
            <path
              d="M314.81 346.52C335.25 346.52 351.82 331.069 351.82 312.01C351.82 292.951 335.25 277.5 314.81 277.5C294.37 277.5 277.8 292.951 277.8 312.01C277.8 331.069 294.37 346.52 314.81 346.52Z"
              fill="#44411F"
            />
            {/* Pawn spot 4 — bottom-right fill */}
            <path
              d="M314.81 349.21C335.25 349.21 351.82 333.759 351.82 314.7C351.82 295.641 335.25 280.19 314.81 280.19C294.37 280.19 277.8 295.641 277.8 314.7C277.8 333.759 294.37 349.21 314.81 349.21Z"
              fill="#FFFECE"
            />
          </>
        )}

        {/* Cloud top-right large */}
        <path
          d="M333.44 94.7901C333.44 84.9301 344.76 76.9301 358.72 76.9301C361.62 76.9301 364.4 77.2801 366.99 77.9101C367.66 69.7701 375.3 63.3501 384.64 63.3501C392.28 63.3501 398.79 67.6501 401.27 73.6801C403.56 73.0201 406.05 72.6601 408.66 72.6601C419.83 72.6601 428.89 79.2801 428.89 87.4401C428.89 88.9201 428.59 90.3501 428.04 91.7001C436.2 94.6101 441.85 100.88 441.85 108.16C441.85 118.2 431.1 126.33 417.83 126.33C409.98 126.33 403.01 123.48 398.63 119.08C394.45 120.81 389.43 121.82 384.01 121.82C373.54 121.82 364.5 118.05 360.29 112.62C359.77 112.65 359.25 112.66 358.72 112.66C344.76 112.66 333.44 104.67 333.44 94.8001V94.7901Z"
          fill={`url(#${prefix}P8)`}
        />
        {/* Cloud right-edge small */}
        <path
          d="M441.85 144.73V177.53C438.84 176.81 436.24 175.39 434.39 173.53C431.8 174.61 428.67 175.23 425.31 175.23C418.8 175.23 413.18 172.89 410.57 169.51C410.24 169.53 409.92 169.54 409.59 169.54C400.91 169.54 393.88 164.57 393.88 158.44C393.88 152.31 400.91 147.34 409.59 147.34C411.39 147.34 413.12 147.55 414.73 147.95C415.14 142.88 419.9 138.89 425.7 138.89C430.45 138.89 434.49 141.57 436.03 145.31C437.46 144.9 439.01 144.68 440.63 144.68C441.04 144.68 441.45 144.68 441.85 144.73Z"
          fill={`url(#${prefix}P9)`}
        />
        {/* Cloud right small puff */}
        <path
          d="M391.09 223.25C389.4 219.22 392.66 214.01 398.36 211.62C399.54 211.12 400.74 210.79 401.91 210.6C400.79 207.16 402.81 203.22 406.63 201.62C409.75 200.31 413.15 200.95 415.2 202.99C416.02 202.33 416.98 201.75 418.05 201.3C422.62 199.38 427.45 200.54 428.85 203.88C429.1 204.49 429.23 205.12 429.23 205.77C433.06 205.56 436.45 207.16 437.7 210.13C439.42 214.23 436.42 219.4 431 221.67C427.79 223.02 424.45 223.05 421.91 222C420.5 223.42 418.62 224.7 416.4 225.62C412.12 227.42 407.78 227.43 405.13 225.92C404.92 226.02 404.71 226.11 404.5 226.2C398.79 228.59 392.79 227.27 391.1 223.24L391.09 223.25Z"
          fill={`url(#${prefix}P10)`}
        />
        {/* Cloud bottom-left large */}
        <path
          d="M53.73 402.94C53.73 393.08 65.05 385.08 79.01 385.08C81.91 385.08 84.69 385.43 87.28 386.06C87.95 377.92 95.59 371.5 104.93 371.5C112.57 371.5 119.08 375.8 121.56 381.83C123.85 381.17 126.34 380.81 128.95 380.81C140.12 380.81 149.18 387.43 149.18 395.59C149.18 397.07 148.88 398.5 148.33 399.85C156.49 402.76 162.14 409.03 162.14 416.31C162.14 426.35 151.39 434.48 138.12 434.48C130.27 434.48 123.3 431.63 118.92 427.23C114.74 428.96 109.72 429.97 104.3 429.97C93.83 429.97 84.79 426.2 80.58 420.77C80.06 420.8 79.54 420.81 79.01 420.81C65.05 420.81 53.73 412.82 53.73 402.95V402.94Z"
          fill={`url(#${prefix}P11)`}
        />
        {/* Cloud small top-center */}
        <path
          d="M290.3 90.7C289.23 87.45 292.09 83.59 296.69 82.08C297.64 81.77 298.6 81.58 299.52 81.51C298.86 78.76 300.68 75.81 303.75 74.8C306.27 73.97 308.88 74.68 310.34 76.4C311.02 75.93 311.8 75.54 312.66 75.26C316.34 74.05 320.04 75.25 320.92 77.94C321.08 78.43 321.14 78.93 321.1 79.44C324.1 79.51 326.64 80.97 327.43 83.36C328.52 86.66 325.86 90.51 321.49 91.94C318.91 92.79 316.3 92.61 314.38 91.63C313.19 92.65 311.65 93.53 309.86 94.11C306.41 95.24 303.03 94.98 301.05 93.65C300.88 93.71 300.71 93.77 300.54 93.83C295.94 95.34 291.35 93.94 290.28 90.69L290.3 90.7Z"
          fill={`url(#${prefix}P12)`}
        />
        {/* Cloud small bottom-center */}
        <path
          d="M170.59 407.68C169.52 404.43 172.38 400.57 176.98 399.06C177.93 398.75 178.89 398.56 179.81 398.49C179.15 395.74 180.97 392.79 184.04 391.78C186.56 390.95 189.17 391.66 190.63 393.38C191.31 392.91 192.09 392.52 192.95 392.24C196.63 391.03 200.33 392.23 201.21 394.92C201.37 395.41 201.43 395.91 201.39 396.42C204.39 396.49 206.93 397.95 207.72 400.34C208.81 403.64 206.15 407.49 201.78 408.92C199.2 409.77 196.59 409.59 194.67 408.61C193.48 409.63 191.94 410.51 190.15 411.09C186.7 412.22 183.32 411.96 181.34 410.63C181.17 410.69 181 410.75 180.83 410.81C176.23 412.32 171.64 410.92 170.57 407.67L170.59 407.68Z"
          fill={`url(#${prefix}P13)`}
        />
        {/* Cloud tiny left-center */}
        <path
          d="M74.07 351.06C73.27 348.63 75.41 345.74 78.86 344.6C79.57 344.36 80.29 344.22 80.98 344.17C80.48 342.11 81.85 339.9 84.15 339.14C86.04 338.52 87.99 339.05 89.09 340.34C89.6 339.99 90.19 339.7 90.83 339.49C93.59 338.58 96.36 339.49 97.02 341.5C97.14 341.87 97.18 342.24 97.16 342.62C99.41 342.67 101.31 343.77 101.9 345.56C102.71 348.04 100.72 350.92 97.45 351.99C95.51 352.63 93.56 352.49 92.12 351.76C91.23 352.53 90.07 353.18 88.74 353.62C86.16 354.47 83.62 354.27 82.14 353.27C82.01 353.32 81.89 353.36 81.76 353.41C78.31 354.54 74.87 353.49 74.07 351.06Z"
          fill={`url(#${prefix}P14)`}
        />
        {/* Cloud left-edge partial */}
        <path
          d="M84.58 325.79C80.65 327.09 76.69 326.81 73.78 325.33C71.97 326.88 69.62 328.21 66.91 329.1C61.67 330.83 56.53 330.43 53.53 328.4C53.27 328.5 53.01 328.59 52.75 328.68C51.21 329.19 49.68 329.47 48.21 329.56V310.43C49.21 310.17 50.2 310.01 51.16 309.93C50.16 305.75 52.93 301.27 57.6 299.74C61.42 298.48 65.39 299.56 67.62 302.17C68.66 301.46 69.85 300.87 71.15 300.44C76.75 298.6 82.37 300.42 83.71 304.51C83.96 305.25 84.04 306.01 83.99 306.78C88.55 306.89 92.41 309.1 93.61 312.74C95.26 317.77 91.22 323.61 84.58 325.79Z"
          fill={`url(#${prefix}P15)`}
        />

        {/* Gold corner decoration top-left — diagonal 1 flat */}
        <path
          d="M41.1 77.3999V80.3999L61.52 106.43L74.41 107.32L93.52 124.51L102.88 122.62V119.62L72.41 104.51L63.08 102.73L41.1 77.3999Z"
          fill="#D3A04F"
        />
        {/* Gold corner decoration top-left — diagonal 1 shadow */}
        <path
          d="M102.88 122.62L81.55 103.95H63.33L41.1 80.3999L60.73 108.62H74.36L93.62 126.25L102.88 122.62Z"
          fill="#D3A04F"
        />
        {/* Gold corner decoration top-left — diagonal 1 gradient */}
        <path
          d="M102.88 119.62L81.55 100.95H63.33L41.1 77.3999L60.73 105.62H74.36L93.62 123.25L102.88 119.62Z"
          fill={`url(#${prefix}P16)`}
        />
        {/* Gold corner decoration top-left — diagonal 2 flat */}
        <path
          d="M65.4 53.76V55.87L89.45 77.4L92.86 98.67L108.66 115L111.25 108.39V106.28L92.74 90.39L91.93 74.84L65.4 53.76Z"
          fill="#D3A04F"
        />
        {/* Gold corner decoration top-left — diagonal 2 shadow */}
        <path
          d="M107.62 117.65L88.96 96.3201V78.0901L65.4 55.8701L93.62 75.5001V89.1301L111.25 108.39L107.62 117.65Z"
          fill="#D3A04F"
        />
        {/* Gold corner decoration top-left — diagonal 2 gradient */}
        <path
          d="M107.62 115.54L88.96 94.2V75.98L65.4 53.76L93.62 73.39V87.02L111.25 106.28L107.62 115.54Z"
          fill={`url(#${prefix}P17)`}
        />

        {/* Gold corner decoration bottom-left — vertical flat */}
        <path
          d="M67.64 441.65H70.64L96.68 421.23L97.57 408.34L114.75 389.23L112.87 379.87H109.87L94.75 410.34L92.97 419.67L67.64 441.65Z"
          fill="#D3A04F"
        />
        {/* Gold corner decoration bottom-left — vertical shadow */}
        <path
          d="M112.87 379.87L94.2 401.2V419.42L70.64 441.65L98.87 422.02V408.39L116.5 389.13L112.87 379.87Z"
          fill="#D3A04F"
        />
        {/* Gold corner decoration bottom-left — vertical gradient */}
        <path
          d="M109.87 379.87L91.2 401.2V419.42L67.64 441.65L95.87 422.02V408.39L113.5 389.13L109.87 379.87Z"
          fill={`url(#${prefix}P18)`}
        />
        {/* Gold corner decoration bottom-left — horizontal flat */}
        <path
          d="M44.01 417.35H46.12L67.64 393.3L88.91 389.89L105.25 374.09L98.64 371.5H96.53L80.64 390.01L65.09 390.82L44.01 417.35Z"
          fill="#D3A04F"
        />
        {/* Gold corner decoration bottom-left — horizontal shadow */}
        <path
          d="M107.9 375.13L86.56 393.79H68.34L46.12 417.35L65.75 389.13H79.38L98.64 371.5L107.9 375.13Z"
          fill="#D3A04F"
        />
        {/* Gold corner decoration bottom-left — horizontal gradient */}
        <path
          d="M105.78 375.13L84.45 393.79H66.23L44.01 417.35L63.64 389.13H77.27L96.53 371.5L105.78 375.13Z"
          fill={`url(#${prefix}P19)`}
        />

        {/* Gold corner decoration bottom-right — vertical flat */}
        <path
          d="M423.55 441.65H420.55L394.52 421.23L393.63 408.34L376.44 389.23L378.33 379.87H381.33L396.44 410.34L398.22 419.67L423.55 441.65Z"
          fill="#D3A04F"
        />
        {/* Gold corner decoration bottom-right — vertical shadow */}
        <path
          d="M378.33 379.87L397 401.2V419.42L420.55 441.65L392.33 422.02V408.39L374.7 389.13L378.33 379.87Z"
          fill="#D3A04F"
        />
        {/* Gold corner decoration bottom-right — vertical gradient */}
        <path
          d="M381.33 379.87L400 401.2V419.42L423.55 441.65L395.33 422.02V408.39L377.7 389.13L381.33 379.87Z"
          fill={`url(#${prefix}P20)`}
        />
        {/* Gold corner decoration bottom-right — horizontal flat */}
        <path
          d="M447.19 417.35H445.08L423.55 393.3L402.28 389.89L385.95 374.09L392.56 371.5H394.67L410.55 390.01L426.11 390.82L447.19 417.35Z"
          fill="#D3A04F"
        />
        {/* Gold corner decoration bottom-right — horizontal shadow */}
        <path
          d="M383.3 375.13L404.63 393.79H422.85L445.08 417.35L425.45 389.13H411.82L392.56 371.5L383.3 375.13Z"
          fill="#D3A04F"
        />
        {/* Gold corner decoration bottom-right — horizontal gradient */}
        <path
          d="M385.41 375.13L406.74 393.79H424.97L447.19 417.35L427.56 389.13H413.93L394.67 371.5L385.41 375.13Z"
          fill={`url(#${prefix}P21)`}
        />

        {/* Gold corner decoration top-right — diagonal 1 flat */}
        <path
          d="M446.02 77.8799V80.8799L425.6 106.92L412.71 107.8L393.6 124.99L384.24 123.1V120.1L414.71 104.99L424.04 103.21L446.02 77.8799Z"
          fill="#D3A04F"
        />
        {/* Gold corner decoration top-right — diagonal 1 shadow */}
        <path
          d="M384.24 123.1L405.57 104.44H423.8L446.02 80.8799L426.39 109.1H412.76L393.5 126.73L384.24 123.1Z"
          fill="#D3A04F"
        />
        {/* Gold corner decoration top-right — diagonal 1 gradient */}
        <path
          d="M384.24 120.1L405.57 101.44H423.8L446.02 77.8799L426.39 106.1H412.76L393.5 123.73L384.24 120.1Z"
          fill={`url(#${prefix}P22)`}
        />
        {/* Gold corner decoration top-right — diagonal 2 flat */}
        <path
          d="M421.72 54.24V56.36L397.67 77.88L394.26 99.15L378.46 115.48L375.87 108.87V106.76L394.38 90.88L395.19 75.32L421.72 54.24Z"
          fill="#D3A04F"
        />
        {/* Gold corner decoration top-right — diagonal 2 shadow */}
        <path
          d="M379.5 118.13L398.17 96.8001V78.5801L421.72 56.3601L393.5 75.9901V89.6101L375.87 108.87L379.5 118.13Z"
          fill="#D3A04F"
        />
        {/* Gold corner decoration top-right — diagonal 2 gradient */}
        <path
          d="M379.5 116.02L398.17 94.69V76.47L421.72 54.24L393.5 73.87V87.5L375.87 106.76L379.5 116.02Z"
          fill={`url(#${prefix}P23)`}
        />

        {/* Gold star top-right — flat */}
        <path
          d="M437.55 54.64L420.35 46.73L428.26 63.93L420.35 81.13L437.55 73.22L454.75 81.13L446.84 63.93L454.75 46.73L437.55 54.64Z"
          fill="#D3A04F"
        />
        {/* Gold star top-right — cross */}
        <path
          d="M454.75 81.13V77.4L444.32 60.2L454.75 46.73V43L434.17 55.3L420.35 43V46.73L432.1 66.12L420.35 77.4V81.13L441.51 72.78L454.75 81.13Z"
          fill="#D3A04F"
        />
        {/* Gold star top-right — gradient */}
        <path
          d="M437.55 50.91L420.35 43L428.26 60.2L420.35 77.4L437.55 69.49L454.75 77.4L446.84 60.2L454.75 43L437.55 50.91Z"
          fill={`url(#${prefix}P24)`}
        />

        {/* Gold star bottom-left — flat */}
        <path
          d="M48.2 447.53L65.4 455.43L57.49 438.24L65.4 421.04L48.2 428.95L31 421.04L38.91 438.24L31 455.43L48.2 447.53Z"
          fill="#D3A04F"
        />
        {/* Gold star bottom-left — cross */}
        <path
          d="M31 421.04V424.77L41.43 441.96L31 455.43V459.16L51.58 446.86L65.4 459.16V455.43L53.65 436.05L65.4 424.77V421.04L44.24 429.38L31 421.04Z"
          fill="#D3A04F"
        />
        {/* Gold star bottom-left — gradient */}
        <path
          d="M48.2 451.25L65.4 459.16L57.49 441.96L65.4 424.77L48.2 432.67L31 424.77L38.91 441.96L31 459.16L48.2 451.25Z"
          fill={`url(#${prefix}P25)`}
        />

        {/* Gold star bottom-right — flat */}
        <path
          d="M437.55 447.53L420.35 455.43L428.26 438.24L420.35 421.04L437.55 428.95L454.75 421.04L446.84 438.24L454.75 455.43L437.55 447.53Z"
          fill="#D3A04F"
        />
        {/* Gold star bottom-right — cross */}
        <path
          d="M454.75 421.04V424.77L444.32 441.96L454.75 455.43V459.16L434.17 446.86L420.35 459.16V455.43L432.1 436.05L420.35 424.77V421.04L441.51 429.38L454.75 421.04Z"
          fill="#D3A04F"
        />
        {/* Gold star bottom-right — gradient */}
        <path
          d="M437.55 451.25L420.35 459.16L428.26 441.96L420.35 424.77L437.55 432.67L454.75 424.77L446.84 441.96L454.75 459.16L437.55 451.25Z"
          fill={`url(#${prefix}P26)`}
        />
      </g>

      {/* Left badge display when player has voluntarily left the game */}
      {hasLeft && (
        <g>
          {/* Circle background - grey tint */}
          <circle cx="245" cy="246" r="100" fill="#666" />
          <circle cx="245" cy="246" r="85" fill="#444" />

          {/* Exit/Leave door icon */}
          <g transform="translate(185, 186)">
            {/* Door frame */}
            <rect
              x="15"
              y="10"
              width="50"
              height="100"
              rx="4"
              fill="#888"
              stroke="#aaa"
              strokeWidth="3"
            />
            {/* Door */}
            <rect x="20" y="15" width="40" height="90" rx="2" fill="#666" />
            {/* Door handle */}
            <circle cx="52" cy="60" r="4" fill="#ccc" />
            {/* Arrow pointing out */}
            <path
              d="M75 60 L100 60 M90 50 L100 60 L90 70"
              stroke="#fff"
              strokeWidth="6"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </g>
        </g>
      )}

      {/* Rank badge display when player has finished (but not left) */}
      {playerRank !== undefined && !hasLeft && (
        <g>
          {/* Trophy circle background */}
          <circle cx="245" cy="246" r="100" fill="#FFFECE" />
          <circle cx="245" cy="246" r="90" fill={gradientMid} />

          {/* Trophy icon and rank text */}
          <text
            x="245"
            y="227"
            textAnchor="middle"
            fontSize="72"
            aria-label={`Rank ${playerRank.toString()} trophy`}
          >
            {getRankIcon(playerRank)}
          </text>
          <text x="245" y="297" textAnchor="middle" fontSize="48" fontWeight="bold" fill="#fff">
            {getRankOrdinal(playerRank)}
          </text>
        </g>
      )}

      {/* Render pawns conditionally (hidden when player has finished or left) */}
      {playerRank === undefined && !hasLeft && (
        <>
          {renderPawnAtPosition(
            'pawn1',
            pawn1,
            PawnComponent,
            {
              isActive: pawn1Active,
              isHovered: pawn1Hovered,
              isSelected: pawn1Selected,
              isMoveable: pawn1Moveable
            },
            onPawn1Click,
            shouldAnimatePawns
          )}
          {renderPawnAtPosition(
            'pawn2',
            pawn2,
            PawnComponent,
            {
              isActive: pawn2Active,
              isHovered: pawn2Hovered,
              isSelected: pawn2Selected,
              isMoveable: pawn2Moveable
            },
            onPawn2Click,
            shouldAnimatePawns
          )}
          {renderPawnAtPosition(
            'pawn3',
            pawn3,
            PawnComponent,
            {
              isActive: pawn3Active,
              isHovered: pawn3Hovered,
              isSelected: pawn3Selected,
              isMoveable: pawn3Moveable
            },
            onPawn3Click,
            shouldAnimatePawns
          )}
          {renderPawnAtPosition(
            'pawn4',
            pawn4,
            PawnComponent,
            {
              isActive: pawn4Active,
              isHovered: pawn4Hovered,
              isSelected: pawn4Selected,
              isMoveable: pawn4Moveable
            },
            onPawn4Click,
            shouldAnimatePawns
          )}
        </>
      )}
    </svg>
  );
});

export default CelesteBaseComponent;
